import { createInitialOwnerAdminState } from "./mock-data.ts";
import type {
  AnalyticsEventFilters,
  AnalyticsEventRecord,
  AnalyticsEventsPage,
  AnalyticsSummary,
  AnalyticsSummaryRange,
  AuditLogEntry,
  BranchConfig,
  MenuCategory,
  MenuItemRecord,
  OwnerAdminState,
  OwnerPrincipal,
  SocialFlags,
  StaffAccount,
  StaffRole,
  TableConfig,
  VenueConfig,
  ZoneConfig,
} from "./types.ts";

let state: OwnerAdminState = createInitialOwnerAdminState();
let counter = 100;

function clone<T>(value: T): T {
  return structuredClone(value) as T;
}

function now(): string {
  return new Date().toISOString();
}

function audit(
  action: string,
  entity: string,
  entityId: string,
  metadata: AuditLogEntry["metadata"] = {},
): AuditLogEntry {
  const entry = {
    id: `aud-${counter++}`,
    actor: "demo-owner",
    action,
    entity,
    entityId,
    createdAt: now(),
    metadata: { ...metadata, pii: false },
  };
  state.auditLog.unshift(entry);
  return entry;
}

function token(id: string): string {
  return `qr_${id}_${Date.now().toString(36)}_${Math.round(Math.random() * 999_999).toString(36)}`;
}

export function resetOwnerAdminMockState(): OwnerAdminState {
  state = createInitialOwnerAdminState();
  counter = 100;
  return getOwnerAdminState();
}

export function getOwnerAdminState(): OwnerAdminState {
  return clone(state);
}

export function loginOwner(email: string, password: string): OwnerPrincipal | null {
  if (!password) return null;
  return clone(
    state.principals.find((principal) => principal.email.toLowerCase() === email.toLowerCase()) ??
      state.principals[0] ??
      null,
  );
}

export function switchVenue(venueId: string): OwnerAdminState {
  if (!state.venues.some((venue) => venue.id === venueId)) throw new Error("VENUE_NOT_FOUND");
  state.currentVenueId = venueId;
  audit("venue_switched", "Venue", venueId);
  return getOwnerAdminState();
}

export function updateVenue(input: Partial<VenueConfig>): VenueConfig {
  const venue = state.venues.find((item) => item.id === (input.id ?? state.currentVenueId));
  if (!venue) throw new Error("VENUE_NOT_FOUND");
  Object.assign(venue, input, { featureFlags: input.featureFlags ?? venue.featureFlags });
  audit("venue_updated", "Venue", venue.id);
  return clone(venue);
}

export function toggleVenueFeature(key: keyof SocialFlags, enabled: boolean): VenueConfig {
  const venue = state.venues.find((item) => item.id === state.currentVenueId);
  if (!venue) throw new Error("VENUE_NOT_FOUND");
  venue.featureFlags[key] = enabled;
  for (const branch of state.branches.filter((item) => item.venueId === venue.id)) {
    if (!enabled) branch.featureOverrides[key] = false;
  }
  audit("venue_feature_toggled", "Venue", venue.id, { feature: key, enabled });
  return clone(venue);
}

export function createBranch(input: Partial<BranchConfig>): BranchConfig {
  const branch = {
    id: `branch-${counter++}`,
    venueId: input.venueId ?? state.currentVenueId,
    name: input.name ?? "New Branch",
    nameTh: input.nameTh ?? "สาขาใหม่",
    address: input.address ?? "Address pending",
    addressTh: input.addressTh ?? "รอที่อยู่",
    operatingHours: input.operatingHours ?? "18:00-00:00",
    active: input.active ?? true,
    featureOverrides: input.featureOverrides ?? { ...state.venues[0]!.featureFlags },
  };
  state.branches.unshift(branch);
  audit("branch_created", "Branch", branch.id);
  return clone(branch);
}

export function updateBranch(branchId: string, input: Partial<BranchConfig>): BranchConfig {
  const branch = state.branches.find((item) => item.id === branchId);
  if (!branch) throw new Error("BRANCH_NOT_FOUND");
  Object.assign(branch, input);
  audit("branch_updated", "Branch", branch.id);
  return clone(branch);
}

export function createZone(input: Partial<ZoneConfig>): ZoneConfig {
  const zone = {
    id: `zone-${counter++}`,
    branchId: input.branchId ?? state.branches[0]!.id,
    name: input.name ?? "New Zone",
    nameTh: input.nameTh ?? "โซนใหม่",
    sortOrder: input.sortOrder ?? state.zones.length + 1,
    adjacencyHint: input.adjacencyHint ?? "Around grouping hint",
  };
  state.zones.push(zone);
  audit("zone_created", "Zone", zone.id);
  return clone(zone);
}

export function updateZone(zoneId: string, input: Partial<ZoneConfig>): ZoneConfig {
  const zone = state.zones.find((item) => item.id === zoneId);
  if (!zone) throw new Error("ZONE_NOT_FOUND");
  Object.assign(zone, input);
  audit("zone_updated", "Zone", zone.id);
  return clone(zone);
}

export function deleteZone(zoneId: string): { deleted: true } {
  const zone = state.zones.find((item) => item.id === zoneId);
  if (!zone) throw new Error("ZONE_NOT_FOUND");
  const hasTables = state.tables.some((table) => table.zoneId === zoneId);
  if (hasTables) throw new Error("ZONE_NOT_EMPTY");
  state.zones = state.zones.filter((item) => item.id !== zoneId);
  audit("zone_deleted", "Zone", zoneId);
  return { deleted: true };
}

export function createTable(input: Partial<TableConfig>): TableConfig {
  const table = {
    id: `tbl-${counter++}`,
    branchId: input.branchId ?? state.branches[0]!.id,
    zoneId: input.zoneId ?? state.zones[0]!.id,
    code: input.code ?? `T${state.tables.length + 1}`,
    label: input.label ?? `Table ${state.tables.length + 1}`,
    capacity: input.capacity ?? 4,
    status: input.status ?? "free",
    orderOnly: input.orderOnly ?? false,
    qrToken: token("new"),
    qrRotatedAt: now(),
    staleQrWarning: false,
  };
  state.tables.push(table);
  audit("table_created", "Table", table.id);
  return clone(table);
}

export function updateTable(tableId: string, input: Partial<TableConfig>): TableConfig {
  const table = state.tables.find((item) => item.id === tableId);
  if (!table) throw new Error("TABLE_NOT_FOUND");
  Object.assign(table, input);
  audit("table_updated", "Table", table.id);
  return clone(table);
}

export function rotateTableQr(tableId: string): TableConfig {
  const table = state.tables.find((item) => item.id === tableId);
  if (!table) throw new Error("TABLE_NOT_FOUND");
  table.qrToken = token(table.id);
  table.qrRotatedAt = now();
  table.staleQrWarning = false;
  audit("qr_rotated", "Table", table.id, { opaqueToken: true });
  return clone(table);
}

export function deactivateTable(tableId: string): TableConfig {
  const table = state.tables.find((item) => item.id === tableId);
  if (!table) throw new Error("TABLE_NOT_FOUND");
  // Mock parity with the backend's soft-deactivate guard: never hard-delete
  // a table row (check-ins/orders carry history via it).
  table.status = "disabled";
  audit("table_deactivated", "Table", table.id);
  return clone(table);
}

export function createMenuCategory(input: Partial<MenuCategory>): MenuCategory {
  const category = {
    id: `cat-${counter++}`,
    name: input.name ?? "New Category",
    nameTh: input.nameTh ?? "หมวดหมู่ใหม่",
    sortOrder: input.sortOrder ?? state.categories.length + 1,
  };
  state.categories.push(category);
  audit("menu_category_created", "MenuCategory", category.id);
  return clone(category);
}

export function updateMenuCategory(categoryId: string, input: Partial<MenuCategory>): MenuCategory {
  const category = state.categories.find((row) => row.id === categoryId);
  if (!category) throw new Error("MENU_CATEGORY_NOT_FOUND");
  Object.assign(category, input);
  audit("menu_category_updated", "MenuCategory", category.id);
  return clone(category);
}

export function deleteMenuCategory(categoryId: string): { deleted: true } {
  const category = state.categories.find((row) => row.id === categoryId);
  if (!category) throw new Error("MENU_CATEGORY_NOT_FOUND");
  const hasItems = state.menuItems.some((item) => item.categoryId === categoryId);
  if (hasItems) throw new Error("CATEGORY_NOT_EMPTY");
  state.categories = state.categories.filter((row) => row.id !== categoryId);
  audit("menu_category_deleted", "MenuCategory", categoryId);
  return { deleted: true };
}

export function createMenuItem(input: Partial<MenuItemRecord>): MenuItemRecord {
  const item = {
    id: `item-${counter++}`,
    categoryId: input.categoryId ?? state.categories[0]!.id,
    name: input.name ?? "New Giftable Highball",
    nameTh: input.nameTh ?? "ไฮบอลใหม่",
    description: input.description ?? "Mock menu item ready for API integration",
    priceMinor: input.priceMinor ?? 24000,
    imageUrl: input.imageUrl,
    available: input.available ?? true,
    giftable: input.giftable ?? true,
    alcohol: input.alcohol ?? true,
    featuredGiftable: input.featuredGiftable ?? false,
    inFlightWarning: input.inFlightWarning,
    modifiers: input.modifiers ?? [],
  };
  state.menuItems.unshift(item);
  audit("menu_item_created", "MenuItem", item.id, {
    giftable: item.giftable,
    alcohol: item.alcohol,
  });
  return clone(item);
}

export function updateMenuItem(itemId: string, input: Partial<MenuItemRecord>): MenuItemRecord {
  const item = state.menuItems.find((row) => row.id === itemId);
  if (!item) throw new Error("MENU_ITEM_NOT_FOUND");
  Object.assign(item, input);
  audit("menu_item_updated", "MenuItem", item.id, {
    giftable: item.giftable,
    alcohol: item.alcohol,
    available: item.available,
  });
  return clone(item);
}

export function deleteMenuItem(itemId: string): { deleted: true } {
  const item = state.menuItems.find((row) => row.id === itemId);
  if (!item) throw new Error("MENU_ITEM_NOT_FOUND");
  // Mock parity with the backend's soft-archive: the row is removed from the
  // active menu view but never hard-deleted (never break historical orders).
  state.menuItems = state.menuItems.filter((row) => row.id !== itemId);
  audit("menu_item_archived", "MenuItem", itemId);
  return { deleted: true };
}

export function createStaff(input: Partial<StaffAccount>): StaffAccount {
  const account = {
    id: `staff-${counter++}`,
    name: input.name ?? "New Staff",
    username: input.username ?? `staff${counter}`,
    staffCode: input.staffCode ?? `S${counter}`,
    active: input.active ?? true,
    branchScope: input.branchScope ?? [state.branches[0]!.id],
    roles: input.roles ?? ["waiter"],
  };
  state.staff.push(account);
  audit("staff_created", "StaffUser", account.id);
  return clone(account);
}

export function updateStaff(staffId: string, input: Partial<StaffAccount>): StaffAccount {
  const account = state.staff.find((item) => item.id === staffId);
  if (!account) throw new Error("STAFF_NOT_FOUND");
  Object.assign(account, input);
  audit("staff_updated", "StaffUser", account.id, {
    active: account.active,
    roles: account.roles.join(","),
  });
  return clone(account);
}

export function assignRoles(staffId: string, roles: StaffRole[]): StaffAccount {
  const account = state.staff.find((item) => item.id === staffId);
  if (!account) throw new Error("STAFF_NOT_FOUND");
  account.roles = roles;
  audit("staff_roles_assigned", "StaffUser", account.id, { roles: roles.join(",") });
  return clone(account);
}

export function resetStaffPin(staffId: string): { staffId: string; generatedPin: string } {
  const account = state.staff.find((item) => item.id === staffId);
  if (!account) throw new Error("STAFF_NOT_FOUND");
  const generatedPin = String(Math.floor(100000 + Math.random() * 900000));
  audit("staff_pin_reset", "StaffUser", account.id);
  return { staffId, generatedPin };
}

export function updateSocialRules(
  input: Partial<OwnerAdminState["socialRules"]>,
): OwnerAdminState["socialRules"] {
  state.socialRules = { ...state.socialRules, ...input, defaultMode: "private" };
  audit("social_rules_updated", "SocialRules", state.currentVenueId);
  return clone(state.socialRules);
}

/** FR-OWN-121: create + open a real `VenueSession` for one branch.
 * Mirrors the backend guard: only one non-closed session per branch. */
export function startTonight(input: {
  branchId?: string;
  label?: string;
  templateId?: string;
}): OwnerAdminState["sessions"][number] {
  const branchId = input.branchId ?? state.branches[0]?.id;
  if (!branchId) throw new Error("BRANCH_NOT_FOUND");
  if (state.sessions.some((session) => session.status !== "closed")) {
    throw new Error("SESSION_ALREADY_ACTIVE");
  }
  const template = input.templateId
    ? state.sessionTemplates.find((item) => item.id === input.templateId)
    : undefined;
  const session = {
    id: `vs-${counter++}`,
    label: input.label?.trim() || template?.label || "Tonight",
    status: "open" as const,
    socialPaused: false,
  };
  state.sessions.unshift(session);
  audit("venue_session_created", "VenueSession", session.id, { branchId });
  return clone(session);
}

export function createSessionTemplate(
  input: Partial<OwnerAdminState["sessionTemplates"][number]>,
): OwnerAdminState["sessionTemplates"][number] {
  const template = {
    id: `tmpl-${counter++}`,
    label: input.label ?? "Tonight",
    branchId: input.branchId ?? state.branches[0]!.id,
    enabledFeatures: input.enabledFeatures ?? { ...state.socialRules },
    lastCallRule: input.lastCallRule ?? "30 minutes before close",
    expectedCloseTime: input.expectedCloseTime ?? "02:00",
  };
  state.sessionTemplates.push(template);
  audit("session_template_created", "SessionTemplate", template.id);
  return clone(template);
}

export function updatePrivacy(
  input: Partial<OwnerAdminState["privacy"]>,
): OwnerAdminState["privacy"] {
  state.privacy = { ...state.privacy, ...input };
  audit("privacy_settings_updated", "PrivacySettings", state.currentVenueId);
  return clone(state.privacy);
}

export function updateRetention(
  input: Partial<OwnerAdminState["retention"]>,
): OwnerAdminState["retention"] {
  state.retention = { ...state.retention, ...input };
  audit("retention_settings_updated", "RetentionSettings", state.currentVenueId);
  return clone(state.retention);
}

// Real (non-legacy) analytics event rows shaped like
// `GET /api/v1/admin/analytics/events` (FR-ANALYTICS-*, AEV-* catalog).
// Independent of the legacy `state.analyticsEvents` mock array, which the
// owner-admin events screen no longer reads (that field mirrored the
// backend's previously hardcoded `analyticsEvents: []`).
const mockAnalyticsEvents: AnalyticsEventRecord[] = [
  {
    id: "aev-real-1",
    eventName: "order_submitted",
    actorType: "customer",
    actorRef: "usr-1",
    properties: { order_ref: "order-1", item_count: 2, total_satang: 24000 },
    venueId: "venue-1",
    branchId: "branch-1",
    venueSessionId: "vs-2026-06-06",
    clientTs: null,
    receivedAt: "2026-06-06T14:05:00.000Z",
  },
  {
    id: "aev-real-2",
    eventName: "gift_sent",
    actorType: "customer",
    actorRef: "usr-2",
    properties: { gift_ref: "gift-1", item_count: 1, total_satang: 12000, has_message: true },
    venueId: "venue-1",
    branchId: "branch-1",
    venueSessionId: "vs-2026-06-06",
    clientTs: null,
    receivedAt: "2026-06-06T14:12:00.000Z",
  },
  {
    id: "aev-real-3",
    eventName: "gift_fulfilled",
    actorType: "staff",
    actorRef: "staff-1",
    properties: { gift_ref: "gift-1", accept_to_fulfilled_ms: 90000 },
    venueId: "venue-1",
    branchId: "branch-1",
    venueSessionId: "vs-2026-06-06",
    clientTs: null,
    receivedAt: "2026-06-06T14:14:00.000Z",
  },
  {
    id: "aev-real-4",
    eventName: "report_submitted",
    actorType: "customer",
    actorRef: "usr-3",
    properties: { report_ref: "report-1", category: "harassment", target_kind: "table" },
    venueId: "venue-1",
    branchId: "branch-1",
    venueSessionId: "vs-2026-06-06",
    clientTs: null,
    receivedAt: "2026-06-06T14:20:00.000Z",
  },
];

export function getAnalyticsSummary(range: AnalyticsSummaryRange = {}): AnalyticsSummary {
  return {
    ...clone(state.analyticsSummary),
    rangeFrom: range.from ?? state.analyticsSummary.rangeFrom,
    rangeTo: range.to ?? state.analyticsSummary.rangeTo,
  };
}

export function listAnalyticsEvents(filters: AnalyticsEventFilters = {}): AnalyticsEventsPage {
  const items = clone(mockAnalyticsEvents).filter(
    (event) => !filters.eventName || event.eventName === filters.eventName,
  );
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 20;
  const total = items.length;
  const start = (page - 1) * limit;
  return { items: items.slice(start, start + limit), page, limit, total };
}
